import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// C:\Ava_Local\Ava_API\avaai_dashboard\frontend\src\pages\education\FlashCardsPage.tsx
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
const pageBg = {
    minHeight: "100vh",
    width: "100%",
    boxSizing: "border-box",
    padding: "16px 12px 16px",
    background: "radial-gradient(circle at top, rgba(0,255,255,0.12) 0%, rgba(0,255,255,0.03) 22%, transparent 42%), linear-gradient(180deg, #03060c 0%, #06101b 48%, #02050a 100%)",
    color: "#ecfaff",
};
const shellStyle = {
    maxWidth: "1400px",
    margin: "0 auto",
};
const panelStyle = {
    borderRadius: "20px",
    padding: "16px 16px",
    background: "rgba(7, 13, 23, 0.78)",
    border: "1px solid rgba(0,255,255,0.16)",
    boxShadow: "0 0 22px rgba(0,255,255,0.06), inset 0 0 18px rgba(255,255,255,0.02)",
    backdropFilter: "blur(10px)",
};
const cardStyle = {
    borderRadius: "16px",
    padding: "14px",
    background: "linear-gradient(180deg, rgba(10,16,28,0.96) 0%, rgba(5,10,18,0.98) 100%)",
    color: "#ecfaff",
    border: "1px solid rgba(90, 220, 255, 0.24)",
    boxShadow: "0 8px 22px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.03)",
};
const controlStyle = {
    background: "rgba(8, 16, 28, 0.92)",
    color: "#ecfaff",
    border: "1px solid rgba(90, 220, 255, 0.24)",
    borderRadius: "12px",
    padding: "10px 12px",
    fontSize: "15px",
    outline: "none",
    minHeight: "44px",
    width: "100%",
    boxSizing: "border-box",
};
const buttonStyle = {
    minHeight: "44px",
    borderRadius: "12px",
    border: "1px solid rgba(90, 220, 255, 0.28)",
    background: "linear-gradient(180deg, rgba(10,18,31,0.96) 0%, rgba(6,12,22,0.98) 100%)",
    color: "#ecfdff",
    fontSize: "14px",
    fontWeight: 800,
    cursor: "pointer",
    padding: "0 14px",
    boxShadow: "0 0 12px rgba(0,255,255,0.13)",
};
function randInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}
function getRanges(operation, grade) {
    const map = {
        "K-2": {
            addition: [0, 20],
            subtraction: [0, 20],
            multiplication: [0, 5],
            division: [1, 5],
        },
        "3-5": {
            addition: [10, 100],
            subtraction: [10, 100],
            multiplication: [2, 12],
            division: [2, 12],
        },
        "6-8": {
            addition: [50, 500],
            subtraction: [50, 500],
            multiplication: [6, 20],
            division: [3, 20],
        },
        "9-12": {
            addition: [100, 1000],
            subtraction: [100, 1000],
            multiplication: [10, 25],
            division: [5, 25],
        },
    };
    const [min, max] = map[grade][operation];
    return { min, max };
}
function buildCard(operation, grade) {
    const { min, max } = getRanges(operation, grade);
    if (operation === "addition") {
        const a = randInt(min, max);
        const b = randInt(min, max);
        return {
            question: `${a} + ${b}`,
            answer: a + b,
            hint: "Try adding the bigger place values first, then the smaller ones.",
            example: `Example: 23 + 15 = (20 + 10) + (3 + 5) = 38.`,
        };
    }
    if (operation === "subtraction") {
        let a = randInt(min, max);
        let b = randInt(min, max);
        if (b > a)
            [a, b] = [b, a];
        return {
            question: `${a} - ${b}`,
            answer: a - b,
            hint: "Start with the larger number and subtract in smaller parts if needed.",
            example: `Example: 42 - 17 = 42 - 10 - 7 = 25.`,
        };
    }
    if (operation === "multiplication") {
        const a = randInt(min, max);
        const b = randInt(min, max);
        return {
            question: `${a} × ${b}`,
            answer: a * b,
            hint: "Think of multiplication as equal groups or repeated addition.",
            example: `Example: 4 × 6 means 4 groups of 6, which equals 24.`,
        };
    }
    const divisor = randInt(min, max);
    const quotient = randInt(min, max);
    const dividend = divisor * quotient;
    return {
        question: `${dividend} ÷ ${divisor}`,
        answer: quotient,
        hint: "Ask what number times the divisor gives the dividend.",
        example: `Example: 24 ÷ 6 = 4 because 6 × 4 = 24.`,
    };
}
function getAvaResponse(isCorrect, reveal, current, userAnswer) {
    if (reveal) {
        return `Here is the answer: ${current.question} = ${current.answer}. ${current.example}`;
    }
    if (isCorrect === true) {
        return "Great work. You got it right. Keep going one step at a time.";
    }
    if (isCorrect === false) {
        return `That is okay. Let’s slow it down and try another way. Hint: ${current.hint} ${current.example}`;
    }
    if (!userAnswer.trim()) {
        return "Enter an answer when you're ready. If you want help, use Hint or Reveal.";
    }
    return "Take your time. You can do this. Focus on one part of the problem at a time.";
}
export default function FlashCardsPage() {
    const navigate = useNavigate();
    const [operation, setOperation] = useState("addition");
    const [grade, setGrade] = useState("K-2");
    const [answer, setAnswer] = useState("");
    const [reveal, setReveal] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [result, setResult] = useState(null);
    const [seed, setSeed] = useState(1);
    const [score, setScore] = useState({ correct: 0, total: 0 });
    const current = useMemo(() => buildCard(operation, grade), [operation, grade, seed]);
    const avaMessage = useMemo(() => getAvaResponse(result, reveal, current, answer), [result, reveal, current, answer]);
    function resetCard(nextSeed = seed) {
        setAnswer("");
        setReveal(false);
        setShowHint(false);
        setResult(null);
        setSeed(nextSeed);
    }
    function handleCheck() {
        const parsed = Number(answer.trim());
        const correct = parsed === current.answer;
        setResult(correct);
        setScore((prev) => ({
            correct: prev.correct + (correct ? 1 : 0),
            total: prev.total + 1,
        }));
    }
    function handleNext() {
        resetCard(seed + 1);
    }
    function handleOperationChange(value) {
        setOperation(value);
        resetCard(seed + 1);
    }
    function handleGradeChange(value) {
        setGrade(value);
        resetCard(seed + 1);
    }
    return (_jsx("div", { style: pageBg, children: _jsxs("div", { style: shellStyle, children: [_jsxs("div", { style: { ...panelStyle, marginBottom: "14px" }, children: [_jsx("div", { style: {
                                fontSize: "11px",
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                color: "#79f3ff",
                                fontWeight: 800,
                                marginBottom: "6px",
                            }, children: "Ava Education" }), _jsx("h1", { style: {
                                margin: 0,
                                fontSize: "clamp(26px, 4.3vw, 48px)",
                                lineHeight: 1,
                                fontWeight: 900,
                                color: "#19e6ff",
                                textShadow: "0 0 16px rgba(0,255,255,0.20)",
                                wordBreak: "break-word",
                            }, children: "Flash Cards" }), _jsx("p", { style: {
                                margin: "8px 0 0",
                                fontSize: "clamp(12px, 1.5vw, 16px)",
                                color: "rgba(236,250,255,0.82)",
                                maxWidth: "820px",
                                lineHeight: 1.4,
                            }, children: "Practice core math skills with Ava's supportive tutor style." })] }), _jsxs("div", { className: "flashcards-layout", style: {
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1.7fr) minmax(300px, 0.85fr)",
                        gap: "14px",
                        alignItems: "start",
                    }, children: [_jsxs("div", { style: cardStyle, children: [_jsxs("div", { style: {
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                                        gap: "10px",
                                        marginBottom: "12px",
                                    }, children: [_jsxs("div", { children: [_jsx("div", { style: { marginBottom: "6px", fontWeight: 800, fontSize: "13px" }, children: "Operation" }), _jsxs("select", { value: operation, onChange: (e) => handleOperationChange(e.target.value), style: controlStyle, children: [_jsx("option", { value: "addition", children: "Addition" }), _jsx("option", { value: "subtraction", children: "Subtraction" }), _jsx("option", { value: "multiplication", children: "Multiplication" }), _jsx("option", { value: "division", children: "Division" })] })] }), _jsxs("div", { children: [_jsx("div", { style: { marginBottom: "6px", fontWeight: 800, fontSize: "13px" }, children: "Grade Level" }), _jsxs("select", { value: grade, onChange: (e) => handleGradeChange(e.target.value), style: controlStyle, children: [_jsx("option", { value: "K-2", children: "K\u20132" }), _jsx("option", { value: "3-5", children: "3\u20135" }), _jsx("option", { value: "6-8", children: "6\u20138" }), _jsx("option", { value: "9-12", children: "9\u201312" })] })] })] }), _jsxs("div", { style: {
                                        ...panelStyle,
                                        marginBottom: "12px",
                                        padding: "14px 12px",
                                        textAlign: "center",
                                    }, children: [_jsx("div", { style: {
                                                fontSize: "11px",
                                                color: "rgba(236,250,255,0.68)",
                                                letterSpacing: "0.18em",
                                                textTransform: "uppercase",
                                                marginBottom: "8px",
                                                fontWeight: 700,
                                            }, children: "Current Problem" }), _jsx("div", { style: {
                                                fontSize: "clamp(34px, 6vw, 70px)",
                                                fontWeight: 900,
                                                color: "#ffffff",
                                                textShadow: "0 0 16px rgba(0,255,255,0.18)",
                                                lineHeight: 1.04,
                                                wordBreak: "break-word",
                                            }, children: current.question })] }), _jsxs("div", { style: { display: "grid", gap: "10px" }, children: [_jsx("input", { type: "number", value: answer, onChange: (e) => setAnswer(e.target.value), placeholder: "Type your answer", style: {
                                                ...controlStyle,
                                                minHeight: "48px",
                                                fontSize: "17px",
                                            } }), _jsxs("div", { style: {
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))",
                                                gap: "8px",
                                            }, children: [_jsx("button", { type: "button", onClick: handleCheck, style: buttonStyle, children: "Check Answer" }), _jsx("button", { type: "button", onClick: () => setShowHint((v) => !v), style: buttonStyle, children: showHint ? "Hide Hint" : "Show Hint" }), _jsx("button", { type: "button", onClick: () => setReveal(true), style: buttonStyle, children: "Reveal" }), _jsx("button", { type: "button", onClick: handleNext, style: buttonStyle, children: "Next Card" })] })] }), _jsxs("div", { style: {
                                        marginTop: "12px",
                                        padding: "10px 12px",
                                        borderRadius: "12px",
                                        background: "rgba(255,255,255,0.03)",
                                        border: "1px solid rgba(90, 220, 255, 0.16)",
                                    }, children: [_jsx("div", { style: { fontWeight: 800, marginBottom: "4px", fontSize: "13px" }, children: "Progress" }), _jsxs("div", { style: { color: "rgba(236,250,255,0.8)", lineHeight: 1.4, fontSize: "13px" }, children: ["Correct: ", score.correct, " / ", score.total] })] })] }), _jsxs("div", { style: { display: "grid", gap: "12px" }, children: [_jsxs("div", { style: cardStyle, children: [_jsx("div", { style: { fontSize: "16px", fontWeight: 900, marginBottom: "6px" }, children: "Ava Tutor" }), _jsx("div", { style: {
                                                lineHeight: 1.55,
                                                color: "rgba(236,250,255,0.84)",
                                                whiteSpace: "pre-wrap",
                                                fontSize: "14px",
                                            }, children: avaMessage })] }), showHint && (_jsxs("div", { style: cardStyle, children: [_jsx("div", { style: { fontSize: "16px", fontWeight: 900, marginBottom: "6px" }, children: "Hint" }), _jsx("div", { style: {
                                                lineHeight: 1.55,
                                                color: "rgba(236,250,255,0.84)",
                                                fontSize: "14px",
                                            }, children: current.hint })] })), reveal && (_jsxs("div", { style: cardStyle, children: [_jsx("div", { style: { fontSize: "16px", fontWeight: 900, marginBottom: "6px" }, children: "Worked Example" }), _jsxs("div", { style: {
                                                lineHeight: 1.55,
                                                color: "rgba(236,250,255,0.84)",
                                                fontSize: "14px",
                                            }, children: ["Answer: ", _jsx("strong", { children: current.answer }), _jsx("br", {}), _jsx("br", {}), current.example] })] })), _jsxs("div", { style: cardStyle, children: [_jsx("div", { style: { fontSize: "16px", fontWeight: 900, marginBottom: "6px" }, children: "Navigation" }), _jsxs("div", { style: {
                                                display: "grid",
                                                gridTemplateColumns: "1fr",
                                                gap: "8px",
                                            }, children: [_jsx("button", { type: "button", onClick: () => navigate("/education"), style: buttonStyle, children: "Back to Education" }), _jsx("button", { type: "button", onClick: () => navigate("/games"), style: buttonStyle, children: "Back to Games" })] })] })] })] }), _jsx("style", { children: `
          @media (max-width: 1120px) {
            .flashcards-layout {
              grid-template-columns: 1fr !important;
            }
          }
        ` })] }) }));
}
